import type { SandboxRenderResult } from "../../../components/UIToolkit/Previews/SandboxedHtmlRenderer";
import type { AiPrintoutShift } from "../../../downloads/aiPrintout/contract";
import type { StoredChatEntry } from "./customPrintoutDraftStorage";
import { buildRepair, renderFailureOf } from "./customPrintoutRepair";
import type { CustomPrintoutGenerationRequest } from "./customPrintoutRequests";
import type { CustomPrintoutCoverage, CustomPrintoutEffort, CustomPrintoutRepair, CustomPrintoutTurn, CustomPrintoutTurnAuthor } from "./customPrintoutTypes";

/**
 * idle → generating → rendering → ready | repairing (once, automatic) → rendering → ready | failed.
 * `idle`: nothing unsaved is on screen. `failed`: the draft on screen doesn't render completely and the one
 * automatic repair is spent; the user can keep chatting.
 */
export type DraftPhase = "idle" | "generating" | "rendering" | "repairing" | "ready" | "failed";

/** The latest finished design job — what Save keeps — the thinking level it was designed at, and what it shows. */
export type CustomPrintoutDraft = { generationId: string; source: string; summary: string; effort: CustomPrintoutEffort; coverage: CustomPrintoutCoverage };

/** Who asked: the viewer (every request of this session), or a saved turn's author (`null` once unknown). */
export type ChatTurnAuthor = { kind: "viewer" } | { kind: "saved"; author: CustomPrintoutTurnAuthor | null };

export type ChatEntry =
	| { kind: "turn"; turn: CustomPrintoutTurn; author: ChatTurnAuthor }
	/** A design request of this session that produced nothing; `request` is "" for the automatic repair. */
	| { kind: "error"; request: string; errorKey: string }
	/**
	 * A request that read as a summary: Annie's description of the sheet she would make, and whether the user made it
	 * one (`accepted`), kept to a full printout (`declined`), or asked something else instead (`dismissed`).
	 */
	| { kind: "question"; request: string; hasAttachment: boolean; summary: string; answer: SummaryQuestionAnswer };

export type SummaryQuestionAnswer = "open" | "accepted" | "declined" | "dismissed";

export type PendingRequest = { request: string; hasAttachment: boolean };

/**
 * The automatic repair the hook must start: what failed, the draft it failed on, that draft's thinking level, and
 * whether it may stay a summary.
 */
export type RepairRequest = { repair: CustomPrintoutRepair; baseGenerationId: string; effort: CustomPrintoutEffort; allowsSummary: boolean };

type SettledPhase = "idle" | "ready" | "failed";

export type DraftState = {
	phase: DraftPhase;
	draft: CustomPrintoutDraft | null;
	/** This session's chat, after the saved printout's own conversation. */
	entries: ChatEntry[];
	pending: PendingRequest | null;
	/** The latest report from the preview, whatever it is drawing. */
	report: SandboxRenderResult;
	/** The preview has started drawing the newest draft, so the next settled report is about it. */
	sawRenderStart: boolean;
	repairUsed: boolean;
	repairRequest: RepairRequest | null;
	/** Where a failed design request returns to. */
	settledPhase: SettledPhase;
	/** The request Annie asked about, sent again as a summary if the user says yes; `null` when nothing is asked. */
	openQuestion: CustomPrintoutGenerationRequest | null;
};

export type DraftAction =
	| { type: "submitted"; pending: PendingRequest }
	| { type: "generationSucceeded"; draft: CustomPrintoutDraft; turn: CustomPrintoutTurn; sameSourceAsShown: boolean; shifts: AiPrintoutShift[] }
	| { type: "generationFailed"; request: string; errorKey: string }
	/** The request read as a summary; `retry` is it, to send again if the user confirms. */
	| { type: "summaryQuestionAsked"; request: string; hasAttachment: boolean; summary: string; retry: CustomPrintoutGenerationRequest }
	| { type: "summaryQuestionAnswered"; accepted: boolean }
	| { type: "reported"; result: SandboxRenderResult; shifts: AiPrintoutShift[] }
	/** The design kept from before a reload, with this session's chat that led to it. */
	| { type: "restored"; draft: CustomPrintoutDraft; entries: StoredChatEntry[]; sameSourceAsShown: boolean; shifts: AiPrintoutShift[] }
	| { type: "reset" };

export const INITIAL_DRAFT_STATE: DraftState = {
	phase: "idle",
	draft: null,
	entries: [],
	pending: null,
	report: { status: "rendering" },
	sawRenderStart: false,
	repairUsed: false,
	repairRequest: null,
	settledPhase: "idle",
	openQuestion: null,
};

/** A finished render of the newest draft: ready when complete, else one automatic repair, else failed. */
const settleDraftRender = (state: DraftState, result: SandboxRenderResult, shifts: AiPrintoutShift[]): DraftState => {
	if (result.status === "rendered" && result.report.ok) return { ...state, report: result, phase: "ready", settledPhase: "ready" };
	const failure = renderFailureOf(result);
	if (failure === null || state.repairUsed || state.draft === null) return { ...state, report: result, phase: "failed", settledPhase: "failed" };
	return {
		...state,
		report: result,
		phase: "repairing",
		repairUsed: true,
		repairRequest: {
			repair: buildRepair(failure, shifts),
			baseGenerationId: state.draft.generationId,
			effort: state.draft.effort,
			allowsSummary: state.draft.coverage === "summary",
		},
	};
};

/**
 * Failures the host sees from outside the frame (it never started, it navigated) or the browser causes. They can
 * arrive without the preview ever saying it started drawing, so they settle a render on their own.
 */
const SETTLES_WITHOUT_RENDER_START: ReadonlySet<string> = new Set(["not-ready", "navigated", "unsupported"]);

/** `next` shows a new draft and waits for the preview's report on it. */
const awaitDraftRender = (state: DraftState, next: DraftState, sameSourceAsShown: boolean, shifts: AiPrintoutShift[]): DraftState => {
	if (!sameSourceAsShown) return next;
	// The preview won't redraw an identical source: a finished report is already about this draft, and one still
	// being drawn will be.
	return state.report.status === "rendering" ? { ...next, sawRenderStart: true } : settleDraftRender(next, state.report, shifts);
};

const succeeded = (state: DraftState, action: Extract<DraftAction, { type: "generationSucceeded" }>): DraftState =>
	awaitDraftRender(
		state,
		{
			...state,
			phase: "rendering",
			draft: action.draft,
			entries: [...state.entries, { kind: "turn", turn: action.turn, author: { kind: "viewer" } }],
			pending: null,
			repairRequest: null,
			sawRenderStart: false,
		},
		action.sameSourceAsShown,
		action.shifts,
	);

/**
 * A design back from before a reload. Its automatic repair, if it had one, already ran then; one that doesn't render
 * completely now (the day changed) is left for the user to fix rather than spending a design they didn't ask for.
 */
const restored = (state: DraftState, action: Extract<DraftAction, { type: "restored" }>): DraftState =>
	// It arrives a moment after the printout opens; a design the user started meanwhile wins.
	state.draft !== null || state.phase !== "idle" || state.entries.length > 0
		? state
		: awaitDraftRender(
				state,
				{
					...state,
					phase: "rendering",
					draft: action.draft,
					entries: action.entries.map((entry): ChatEntry => (entry.kind === "turn" ? { ...entry, author: { kind: "viewer" } } : entry)),
					pending: null,
					repairRequest: null,
					repairUsed: true,
					sawRenderStart: false,
				},
				action.sameSourceAsShown,
				action.shifts,
			);

const failed = (state: DraftState, action: Extract<DraftAction, { type: "generationFailed" }>): DraftState => {
	const entries: ChatEntry[] = [...state.entries, { kind: "error", request: action.request, errorKey: action.errorKey }];
	// The repair is the only request with no draft left to fall back to a working state from.
	const phase = state.phase === "repairing" ? "failed" : state.settledPhase;
	return { ...state, phase, settledPhase: phase === "failed" ? "failed" : state.settledPhase, entries, pending: null, repairRequest: null };
};

const reported = (state: DraftState, action: Extract<DraftAction, { type: "reported" }>): DraftState => {
	const { result } = action;
	if (result.status === "rendering") return { ...state, report: result, sawRenderStart: true };
	const settlesNow = state.sawRenderStart || (result.status === "failed" && SETTLES_WITHOUT_RENDER_START.has(result.kind));
	if (state.phase === "rendering" && settlesNow) return settleDraftRender(state, result, action.shifts);
	// A settled draft follows later redraws (the day's data refreshed, the frame reloaded) without another repair.
	if (state.draft !== null && (state.phase === "ready" || state.phase === "failed")) {
		const phase = result.status === "rendered" && result.report.ok ? "ready" : "failed";
		return { ...state, report: result, phase, settledPhase: phase };
	}
	return { ...state, report: result };
};

/** The open question, if any, closed with `answer`. */
const closeQuestion = (state: DraftState, answer: SummaryQuestionAnswer): DraftState => ({
	...state,
	openQuestion: null,
	entries: state.entries.map((entry) => (entry.kind === "question" && entry.answer === "open" ? { ...entry, answer } : entry)),
});

const questionAsked = (state: DraftState, action: Extract<DraftAction, { type: "summaryQuestionAsked" }>): DraftState => ({
	...state,
	// Nothing was designed: back to wherever the chat was before the request.
	phase: state.settledPhase,
	pending: null,
	openQuestion: action.retry,
	entries: [...state.entries, { kind: "question", request: action.request, hasAttachment: action.hasAttachment, summary: action.summary, answer: "open" }],
});

export const draftReducer = (state: DraftState, action: DraftAction): DraftState => {
	switch (action.type) {
		case "submitted":
			// Asking something else instead of answering leaves Annie's question behind.
			return { ...closeQuestion(state, "dismissed"), phase: "generating", pending: action.pending, repairUsed: false, repairRequest: null };
		case "summaryQuestionAsked":
			return questionAsked(state, action);
		case "summaryQuestionAnswered":
			return closeQuestion(state, action.accepted ? "accepted" : "declined");
		case "generationSucceeded":
			return succeeded(state, action);
		case "generationFailed":
			return failed(state, action);
		case "reported":
			return reported(state, action);
		case "restored":
			return restored(state, action);
		case "reset":
			// The preview keeps drawing whatever it drew, and won't report again for the same source.
			return { ...INITIAL_DRAFT_STATE, report: state.report, sawRenderStart: state.sawRenderStart };
	}
};
